import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import styled from "styled-components";
import { SectionEyebrow, SectionTitle } from "../common/Section";
import releases from "../../../data/releases";

const sortedReleases = [...releases].sort(
  (a, b) => (b.streams || 0) - (a.streams || 0)
);

/* ── styled ── */

const Section = styled.section`
  padding: clamp(4.5rem,7vw,6.5rem) 8vw;
  background: linear-gradient(180deg, rgba(255,255,255,.015), transparent);
  @media (max-width: 480px) { padding-left: 5vw; padding-right: 5vw; }
`;

const Header = styled.div`
  display: grid;
  grid-template-columns: minmax(0,.9fr) minmax(0,1.1fr);
  gap: 24px; align-items: end; margin-bottom: 30px;
  @media (max-width: 1024px) { grid-template-columns: 1fr; }
`;

const Intro = styled.p`max-width: 40rem; color: var(--muted); line-height: 1.8;`;

const Wrapper = styled.div`position: relative;`;

const Row = styled.div`
  display: flex; gap: 22px;
  overflow-x: auto; padding-bottom: 10px;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  &::-webkit-scrollbar { height: 8px; }
  &::-webkit-scrollbar-thumb { background: var(--ink-10); border-radius: 999px; }
`;

const ShelfBtn = styled.button`
  position: absolute; top: 44%; transform: translateY(-50%); z-index: 5;
  width: 46px; height: 46px;
  border: 1px solid var(--ink-12); border-radius: 50%;
  background: rgba(11,11,13,.82); color: var(--ink);
  display: inline-flex; align-items: center; justify-content: center; cursor: pointer;
  &:hover { border-color: rgba(214,179,106,.42); }
  @media (max-width: 1024px) { display: none; }
`;

const Prev = styled(ShelfBtn)`left: -18px;`;
const Next = styled(ShelfBtn)`right: -18px;`;

const Card = styled(Link)`
  flex: 0 0 min(28vw,340px);
  scroll-snap-align: start; display: grid; gap: 14px; color: inherit;
  @media (max-width: 1024px) { flex-basis: 280px; }
  @media (max-width: 768px) { flex-basis: 78vw; }
`;

const Art = styled.div`
  aspect-ratio: 1/1.08; overflow: hidden;
  background: rgba(255,255,255,.03);
  border: 1px solid var(--ink-08);
  img { width: 100%; height: 100%; object-fit: cover; transition: transform .32s ease; }
  ${Card}:hover & img { transform: scale(1.04); }
`;

const Meta = styled.div`
  display: grid; gap: 6px;
  h3 { font-size: 1rem; letter-spacing: .08em; text-transform: uppercase; }
  p { color: var(--muted); line-height: 1.6; font-size: 14px; }
  strong { color: var(--accent); font-size: 13px; letter-spacing: .12em; text-transform: uppercase; }
`;

const Dots = styled.div`display: flex; gap: 8px; justify-content: center; margin-top: 18px;`;
const Dot = styled.button<{ $active?: boolean }>`
  width: ${(p) => (p.$active ? "26px" : "9px")}; height: 9px;
  border-radius: 999px;
  background: ${(p) => p.$active ? "rgba(214,179,106,.95)" : "var(--ink-12)"};
  border: none; cursor: pointer;
  transition: width .2s ease, background .2s ease;
`;

/* ── component ── */

const HomeReleases: React.FC = () => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [page, setPage] = useState(0);
  const [pages, setPages] = useState(1);
  const items = useMemo(() => sortedReleases, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const calc = () => setPages(Math.max(1, Math.ceil(el.scrollWidth / el.clientWidth)));
    calc();
    window.addEventListener("resize", calc);
    return () => window.removeEventListener("resize", calc);
  }, []);

  const onScroll = () => {
    const el = ref.current;
    if (el) setPage(Math.round(el.scrollLeft / el.clientWidth));
  };

  const scrollTo = (i: number) => ref.current?.scrollTo({ left: i * (ref.current?.clientWidth ?? 0), behavior: "smooth" });
  const shift = (d: number) => ref.current?.scrollBy({ left: (ref.current?.clientWidth ?? 0) * 0.72 * d, behavior: "smooth" });

  return (
    <Section>
      <Header>
        <div>
          <SectionEyebrow>Selected work</SectionEyebrow>
          <SectionTitle>Signature releases from the RMS orbit.</SectionTitle>
        </div>
        <Intro>
          Selected catalog highlights. Each release represents RMS-led composition,
          publishing, or creative strategy work.
        </Intro>
      </Header>
      <Wrapper>
        <Prev aria-label="Previous releases" onClick={() => shift(-1)}><FaChevronLeft /></Prev>
        <Next aria-label="Next releases" onClick={() => shift(1)}><FaChevronRight /></Next>
        <Row ref={ref} onScroll={onScroll}>
          {items.map((r) => (
            <Card key={r.id} to={r.link || "/music"}>
              <Art><img src={r.image} alt={r.title} loading="lazy" /></Art>
              <Meta>
                <h3>{r.title}</h3>
                {r.artist && <p style={{ color: "var(--ink)" }}>{r.artist}</p>}
                <p>{r.meta}</p>
                <strong>{r.stat}</strong>
              </Meta>
            </Card>
          ))}
        </Row>
        <Dots>
          {Array.from({ length: pages }).map((_, i) => (
            <Dot key={i} $active={i === page} onClick={() => scrollTo(i)} aria-label={`Page ${i + 1}`} />
          ))}
        </Dots>
      </Wrapper>
    </Section>
  );
};

export default HomeReleases;
